// Your People — full-screen friends list. Scroll: allowed (per UI direction).
// Top: search input filters by name/username. Cards show avatar + handle
// + nights-together + last-seen + top venue (all derived client-side).
// Bottom: pending incoming requests with one-tap accept.
// Add (+) → /your-people/add (placeholder route — Wave C6 owns the flow).

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import {
  type CurrentUser,
  type FriendRequestDto,
  type NightDetailsDto,
  authApi,
  friendsApi,
  meApi,
} from '../../api'
import { Icon } from '../../components/Icon'
import { BackButton, TopBar } from '../../components/TopBar'

export const Route = createFileRoute('/your-people/')({
  component: YourPeopleRoute,
})

const AVATAR_COLORS = [
  'var(--pink)',
  'var(--lime)',
  'var(--teal)',
  'var(--orange)',
  'var(--plum)',
  'var(--acid)',
] as const

function avatarColor(seed: string | null | undefined): string {
  if (!seed) return AVATAR_COLORS[0]
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]
}

function initial(name: string | null | undefined): string {
  if (!name) return '?'
  return name.trim().charAt(0).toUpperCase() || '?'
}

type FriendCard = {
  id: string
  displayName: string
  username: string | null
  friendCode: string | null
  color: string
  letter: string
  nightsTogether: number
  lastSeen: string | null
  topVenue: string | null
}

type Status =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready'
      user: CurrentUser
      friends: FriendCard[]
      incoming: FriendRequestDto[]
    }

function YourPeopleRoute() {
  const navigate = useNavigate()
  const [state, setState] = useState<Status>({ kind: 'loading' })
  const [query, setQuery] = useState('')
  const [accepting, setAccepting] = useState<Set<string>>(new Set())

  async function load() {
    try {
      const me = await authApi.me()
      if (!me) {
        navigate({ to: '/login' })
        return
      }
      const [requests, nights] = await Promise.all([friendsApi.requests(), meApi.history()])
      const incomingPending = requests.incoming.filter((r) => r.status === 'pending')
      const accepted = [
        ...requests.incoming.filter((r) => r.status === 'accepted'),
        ...requests.outgoing.filter((r) => r.status === 'accepted'),
      ]
      const friends = buildFriendCards(me.id, accepted, nights)
      setState({ kind: 'ready', user: me, friends, incoming: incomingPending })
    } catch {
      setState({ kind: 'error' })
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable, load is component-scoped.
  useEffect(() => {
    void load()
  }, [])

  async function acceptRequest(id: string) {
    setAccepting((prev) => new Set(prev).add(id))
    try {
      await friendsApi.accept(id)
      await load()
    } finally {
      setAccepting((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const filtered = useMemo(() => {
    if (state.kind !== 'ready') return []
    const q = query.trim().toLowerCase()
    if (!q) return state.friends
    return state.friends.filter((f) => {
      const hay = `${f.displayName} ${f.username ?? ''} ${f.friendCode ?? ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [state, query])

  const friendCount = state.kind === 'ready' ? state.friends.length : 0
  const incomingCount = state.kind === 'ready' ? state.incoming.length : 0

  return (
    <div className="stumble-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/you' })} />}
        eyebrow={
          state.kind === 'ready'
            ? `${friendCount} stumbler${friendCount === 1 ? '' : 's'}`
            : state.kind === 'error'
              ? 'error'
              : 'loading…'
        }
        title="your people"
        right={
          <button
            type="button"
            className="icon-btn"
            onClick={() => navigate({ to: '/your-people/add' })}
            aria-label="Add a friend"
            data-testid="people-add"
            style={{ background: 'var(--paper)' }}
          >
            <Icon name="plus" size={20} />
          </button>
        }
      />
      <div
        className="screen-body with-nav"
        data-scroll="true"
        style={{ display: 'grid', gap: 10, gridTemplateRows: 'auto 1fr' }}
      >
        <input
          type="search"
          inputMode="search"
          enterKeyHint="search"
          placeholder="search by name or @handle"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter your people"
          data-testid="people-search"
          style={{
            minHeight: 48,
            padding: '0 14px',
            border: '2.5px solid var(--line)',
            borderRadius: 12,
            background: 'var(--paper)',
            color: 'var(--ink)',
            fontWeight: 700,
            fontSize: 16,
            boxShadow: '0 4px 0 var(--shadow-ink)',
          }}
        />

        {state.kind === 'loading' && (
          <div className="card body-sm muted" style={{ textAlign: 'center' }}>
            loading your people…
          </div>
        )}

        {state.kind === 'error' && (
          <div
            className="card"
            data-testid="people-error"
            style={{
              textAlign: 'center',
              padding: 16,
              background: 'var(--pink)',
              color: 'var(--paper)',
              border: '2.5px solid var(--line)',
            }}
          >
            <p className="body-sm" style={{ margin: 0, fontWeight: 700 }}>
              couldn't load your people — pull to refresh
            </p>
            <button
              type="button"
              className="btn full"
              onClick={() => {
                setState({ kind: 'loading' })
                void load()
              }}
              style={{ marginTop: 8, background: 'var(--paper)', color: 'var(--ink)' }}
            >
              retry
            </button>
          </div>
        )}

        {state.kind === 'ready' && (
          <div style={{ display: 'grid', gap: 10 }} data-testid="people-list">
            {filtered.length === 0 && friendCount === 0 && (
              <div
                className="card"
                style={{ textAlign: 'center', padding: 22, display: 'grid', gap: 6 }}
              >
                <p className="display" style={{ fontSize: 30, margin: 0 }}>
                  No stumble buddies yet.
                </p>
                <p className="body-sm muted" style={{ margin: 0 }}>
                  Bars are better with people. Tap + to invite someone.
                </p>
                <button
                  type="button"
                  className="btn pink full"
                  onClick={() => navigate({ to: '/your-people/add' })}
                  style={{ marginTop: 6 }}
                >
                  <Icon name="plus" size={18} /> ADD FRIENDS
                </button>
              </div>
            )}

            {filtered.length === 0 && friendCount > 0 && (
              <p className="body-sm muted" style={{ textAlign: 'center', padding: 14 }}>
                No match for "{query}".
              </p>
            )}

            {filtered.map((f) => (
              <button
                type="button"
                key={f.id}
                onClick={() => navigate({ to: `/your-people/${f.id}` })}
                className="friend-card"
                data-testid={`people-card-${f.id}`}
              >
                <span className="friend-avatar" style={{ background: f.color }} aria-hidden>
                  {f.letter}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="row-between" style={{ gap: 6 }}>
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 24,
                        lineHeight: 1,
                        textTransform: 'lowercase',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {f.displayName}
                    </div>
                    {f.username && (
                      <span
                        className="mono muted"
                        style={{
                          fontSize: 11,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        @{f.username}
                      </span>
                    )}
                  </div>
                  <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <span
                      className="chip"
                      style={{
                        background: f.color,
                        color: 'var(--paper)',
                        padding: '4px 8px',
                        fontSize: 10,
                      }}
                    >
                      {f.nightsTogether} night{f.nightsTogether === 1 ? '' : 's'} together
                    </span>
                    {f.lastSeen && (
                      <span className="chip" style={{ padding: '4px 8px', fontSize: 10 }}>
                        last · {f.lastSeen}
                      </span>
                    )}
                    {f.topVenue && (
                      <span className="chip" style={{ padding: '4px 8px', fontSize: 10 }}>
                        {f.topVenue}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}

            {/* Incoming requests */}
            {incomingCount > 0 && (
              <div
                className="card"
                style={{ display: 'grid', gap: 8, marginTop: 4, padding: 12 }}
                data-testid="people-requests"
              >
                <div className="row-between">
                  <span className="eyebrow">requests</span>
                  <span className="chip solid-pink" style={{ padding: '3px 8px', fontSize: 10 }}>
                    {incomingCount} pending
                  </span>
                </div>
                {state.incoming.map((req) => {
                  const other = req.requester
                  const color = avatarColor(other.friendCode ?? other.id)
                  const isAccepting = accepting.has(req.id)
                  return (
                    <div
                      key={req.id}
                      className="row-between"
                      style={{
                        padding: '8px 10px',
                        border: '2px solid var(--line)',
                        borderRadius: 12,
                        background: 'var(--paper-warm)',
                      }}
                      data-testid={`people-request-${req.id}`}
                    >
                      <div className="row" style={{ gap: 10 }}>
                        <span
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 999,
                            background: color,
                            color: 'var(--paper)',
                            border: '2px solid var(--line)',
                            display: 'grid',
                            placeItems: 'center',
                            fontFamily: 'var(--font-display)',
                            fontSize: 16,
                          }}
                        >
                          {initial(other.displayName)}
                        </span>
                        <div>
                          <div
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontSize: 18,
                              lineHeight: 1,
                              textTransform: 'lowercase',
                            }}
                          >
                            {other.displayName}
                          </div>
                          {other.username && (
                            <div className="mono muted" style={{ fontSize: 11 }}>
                              @{other.username}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="chip solid-lime"
                        onClick={() => acceptRequest(req.id)}
                        disabled={isAccepting}
                        data-testid={`people-accept-${req.id}`}
                        style={{ minHeight: 36 }}
                      >
                        {isAccepting ? '…' : 'accept'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .friend-card {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 12px;
          padding: 12px 14px;
          border: 2.5px solid var(--line);
          border-radius: 16px;
          background: var(--paper);
          box-shadow: 0 5px 0 var(--shadow-ink);
          text-align: left;
          color: var(--ink);
          width: 100%;
        }
        .friend-card:active { transform: translateY(2px); box-shadow: 0 3px 0 var(--shadow-ink); }
        .friend-avatar {
          width: 56px; height: 56px;
          border-radius: 999px;
          color: var(--paper);
          border: 2.5px solid var(--line);
          display: grid; place-items: center;
          font-family: var(--font-display);
          font-size: 24px;
          align-self: center;
        }
      `}</style>
    </div>
  )
}

// Derive per-friend stats from accepted friendships + user's night history.
// Stats are an approximation: "nights together" = nights at the same venue on
// the same day (data the client has). A real "co-attendance" model is a
// follow-up server endpoint.
function buildFriendCards(
  meId: string,
  accepted: FriendRequestDto[],
  _nights: NightDetailsDto[],
): FriendCard[] {
  return accepted.map((req) => {
    const other = req.requesterId === meId ? req.addressee : req.requester
    return {
      id: other.id,
      displayName: other.displayName,
      username: other.username,
      friendCode: other.friendCode,
      color: avatarColor(other.friendCode ?? other.id),
      letter: initial(other.displayName),
      // No co-attendance endpoint yet. Use 0 so we never lie to the user.
      nightsTogether: 0,
      lastSeen: null,
      topVenue: null,
    }
  })
}
